import React from 'react';
import { inject, observer } from 'mobx-react';

import {
  replaceParams,
  getTrans,
  redirectionTo,
  getParamsForWebFormSubmissionCheck,
  formatQueuePosition,
} from '@/utils/tools';
import { Copywriting, TRACKING_TYPE, IS_GREATER_THAN_THRESHOLD } from '@/utils/constants';
import { AgentStore } from '@/stores/agent';
import { AgentLoadingLoader } from '@/components/Common/Loader';
import { getChildInject, bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import ConfirmModal from '@/components/General/ConfirmModal';
import { InitStoreType } from '@/types/store/init';
import { trackingHandler } from '@/utils/btnClickHandler';
import { handleCancelQueue, addBackToChatbotTip } from '@/utils/agent';
import { featureToggle, FEATURE_TOGGLE_TYPE } from '@/utils/featureToggleConfig';
import { getOfflineFormUrlWithParams } from '@/utils/getOfflineFormUrlWithParams';
import { applyChatbotConfig } from '@/config';
import { handleRequest } from '@/utils';
import './index.scss';

interface QueueProps {
  queuePosition: number;
  initialPosition: number;
  agentStore: AgentStore;
  initStore?: InitStoreType;
  userStore: any;
}

const PREFIX = 'agent-queue-container';

@inject(getChildInject('agentStore', 'userStore', 'initStore'))
@observer
export default class Queue extends React.Component<QueueProps> {
  trackingdata = {};
  agentQueueRef = React.createRef<HTMLDivElement>();
  isPremiumOrNoChatbotUser = false;

  constructor(props: QueueProps) {
    super(props);

    const { enable: noRealChatbot } = applyChatbotConfig('noRealChatbot') as { enable: boolean };

    // MX CO CL preminum
    this.isPremiumOrNoChatbotUser = props.initStore?.isPremium || noRealChatbot;
  }

  componentDidUpdate(prevProps: any) {
    const { initialPosition } = this.props;
    const { isShowCancelQueueButton } = this.props.agentStore;
    if (prevProps.initialPosition !== initialPosition && initialPosition) {
      this.updateTrackingNumber();
      if (featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.OVERFLOW_MANAGEMENT) && isShowCancelQueueButton) {
        addObserver(this.agentQueueRef.current);
      }
    }
  }

  componentWillUnmount() {
    removeObserver(this.agentQueueRef.current);
  }

  async componentDidMount() {
    // 修复从floating bubble回来没有update tracknumber的bug
    this.updateTrackingNumber();
    await this.props.userStore.FETCH_ABTEST();
    const { isShowCancelQueueButton, initialPosition } = this.props.agentStore;
    if (
      featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.OVERFLOW_MANAGEMENT)
      && isShowCancelQueueButton
      && initialPosition !== null
    ) {
      addObserver(this.agentQueueRef.current);
    }
  }

  updateTrackingNumber() {
    const { initialPosition, queueXnumber = 0 } = this.props.agentStore;
    this.trackingdata = {
      ...this.trackingdata,
      initial_queue_number: initialPosition,
      is_greater_than_threshold:
        (initialPosition as number) > (queueXnumber as number)
          ? IS_GREATER_THAN_THRESHOLD.YES
          : IS_GREATER_THAN_THRESHOLD.NO,
    };
  }

  handleCancelClick = () => {
    const { isPremiumOrNoChatbotUser } = this;
    this.queueTrackingHandler({ buttonName: 'cancel', trackingname: TRACKING_TYPE.USER_QUEUE_TEXT });
    ConfirmModal({
      className: 'queue-modal',
      title: '',
      content: (
        <div className="content">
          {isPremiumOrNoChatbotUser
            ? getTrans(Copywriting.CHAT_BOT_CLOSE_TIP_CONTENT)
            : getTrans(Copywriting.AGENT_QUEUE_CANCEL_CONFIRM)}
        </div>
      ),
      footer: [
        { value: getTrans(Copywriting.MODAL_CANCEL_TEXT) },
        {
          value: getTrans(Copywriting.MODAL_OK_TEXT),
          type: 'primary',
        },
      ],
      onFooterClick: ({ value }) => {
        if (value === getTrans(Copywriting.MODAL_OK_TEXT)) {
          this.queueTrackingHandler({ buttonName: 'yes', trackingname: TRACKING_TYPE.USER_CANCEL_QUEUE_POPUP });
          handleCancelQueue('user cancel');
          addBackToChatbotTip();
        } else {
          this.queueTrackingHandler({ buttonName: 'cancel', trackingname: TRACKING_TYPE.USER_CANCEL_QUEUE_POPUP });
        }
      },
    });
  };

  handleEmailClick = () => {
    this.queueTrackingHandler({ buttonName: 'email_us', trackingname: TRACKING_TYPE.USER_QUEUE_TEXT });
    ConfirmModal({
      className: 'queue-modal',
      title: '',
      content: <div className="content">{getTrans(Copywriting.AGENT_QUEUE_EMAIL_CONFIRM)} </div>,
      footer: [
        { value: getTrans(Copywriting.MODAL_CANCEL_TEXT) },
        {
          value: getTrans(Copywriting.MODAL_OK_TEXT),
          type: 'primary',
        },
      ],
      onFooterClick: async ({ value }) => {
        if (value === getTrans(Copywriting.MODAL_OK_TEXT)) {
          this.queueTrackingHandler({ buttonName: 'yes', trackingname: TRACKING_TYPE.USER_EMAIL_QUEUE_POPUP });
          this.handleRedirectionBtnClick({ uri_key: await getOfflineFormUrlWithParams() }, this.getWebformSubmission);
          handleCancelQueue('webform');
        } else {
          this.queueTrackingHandler({ buttonName: 'cancel', trackingname: TRACKING_TYPE.USER_EMAIL_QUEUE_POPUP });
        }
      },
    });
  };

  handleRedirectionBtnClick = (uriConfig: any, callback?: Function) => {
    redirectionTo(uriConfig.uri_key);

    bridgeCallFunc(() => {
      const bridgeCallback = async (res: { data: string }) => {
        await callback?.(res);
        window.bridgeUnRegisterHandler('viewWillReappear', bridgeCallback);
      };
      window.bridgeRegisterHandler('viewWillReappear', bridgeCallback);
    });
  };

  getWebformSubmission = async () => {
    const { agentStore } = this.props;
    const res = await handleRequest('getWebformSubmission', getParamsForWebFormSubmissionCheck());
    if (res && !(res instanceof Error)) {
      agentStore.updateIsOfflineWebformSubmitted(res?.is_submission_success);
    }

    addBackToChatbotTip();
  };

  queueTrackingHandler = (props: { buttonName: string; trackingname: string }) => {
    const { buttonName, trackingname } = props;

    trackingHandler({
      trackingname: trackingname,
      trackingdata: {
        ...this.trackingdata,
        button_name: buttonName,
        chat_session_id: this.props.initStore?.sessionId,
      },
    });
  };

  getCancelBtn = () => {
    return (
      <span className="btn" onClick={this.handleCancelClick}>
        {getTrans(Copywriting.AGENT_QUEUE_CANCEL_BTN)}
      </span>
    );
  };

  getEmailBtn = () => {
    return (
      <span className="btn" onClick={this.handleEmailClick}>
        {getTrans(Copywriting.AGENT_QUEUE_EMAIL_BTN)}
      </span>
    );
  };

  getQueueingText = () => {
    const { agentStore } = this.props;
    const {
      position,
      initialPosition,
      isShowCancelQueueButton,
      queueXnumber = 0,
      connectionConfigInfo: { queuePositionUpdateSwitchOpen },
    } = agentStore;

    // 排队为0优化文案
    if (position === 0) {
      return getTrans(Copywriting.AGENT_ENTERING_TIPS);
    }

    const formattedPosition = formatQueuePosition(position);
    const queuePositionUpdateSwitchClose = queuePositionUpdateSwitchOpen === false;

    // 如果在灰度中 & 非shopeeFood & 开关为开启
    if (featureToggle.getFeatureEnable(FEATURE_TOGGLE_TYPE.OVERFLOW_MANAGEMENT) && isShowCancelQueueButton) {
      // if (排队长度 > X) 显示cancel & emailus
      if (initialPosition && initialPosition > queueXnumber) {
        this.trackingdata = { ...this.trackingdata, user_option: 'Cancel+Email Us' };

        if (queuePositionUpdateSwitchClose) {
          return replaceParams(getTrans(Copywriting.AGENT_QUEUE_LONG_TIME_WITH_CANCEL_EMAIL), {
            agent_queue_cancel_btn: this.getCancelBtn(),
            agent_queue_email_btn: this.getEmailBtn(),
          });
        }

        return replaceParams(getTrans(Copywriting.AGENT_QUEUE_LONG_TIPS), {
          queuePosition: formattedPosition,
          agent_queue_cancel_btn: this.getCancelBtn(),
          agent_queue_email_btn: this.getEmailBtn(),
        });
      }

      this.trackingdata = { ...this.trackingdata, user_option: 'Cancel' };

      if (queuePositionUpdateSwitchClose) {
        return replaceParams(getTrans(Copywriting.AGENT_QUEUE_LONG_TIME_WITH_CANCEL), { agent_queue_cancel_btn: this.getCancelBtn() });
      }

      // 仅支持取消功能
      return replaceParams(getTrans(Copywriting.AGENT_QUEUE_TIPS), {
        queuePosition: formattedPosition,
        agent_queue_cancel_btn: this.getCancelBtn(),
      });
    }

    if (queuePositionUpdateSwitchClose) {
      return getTrans(Copywriting.AGENT_QUEUE_LONG_TIME);
    }

    return replaceParams(getTrans(Copywriting.QUEUE_TIPS), { queuePosition: formattedPosition });
  };

  render() {
    const { agentStore } = this.props;
    const { loadingInAgent, initialPosition, queueXnumber = 0 } = agentStore;
    const is_greater_than_threshold
      = initialPosition && initialPosition > (queueXnumber as number)
        ? IS_GREATER_THAN_THRESHOLD.YES
        : IS_GREATER_THAN_THRESHOLD.NO;
    const trackingdata = {
      user_option: is_greater_than_threshold === IS_GREATER_THAN_THRESHOLD.YES ? 'Cancel+Email Us' : 'Cancel',
      initial_queue_number: initialPosition,
      is_greater_than_threshold,
    };

    return (
      <div
        className={PREFIX}
        ref={this.agentQueueRef}
        data-trackingname={TRACKING_TYPE.USER_QUEUE_TEXT_IMPRESS}
        data-trackingdata={JSON.stringify(trackingdata)}
      >
        <div className="queue">
          <AgentLoadingLoader />
          <span>{loadingInAgent ? getTrans(Copywriting.LOADING_IN_AGENT_TEXT) : this.getQueueingText()}</span>
        </div>
      </div>
    );
  }
}
